import { log } from '@/logger/logger.ts';
import { ModelType } from '@/enums/model-type.enum';
import { AiModelData, ModelBaseNodeData } from '@/types/nodes/model';
import { urlCanParse } from './urls';
import type {
  AiModelInfo,
  AudioFile,
  BlobstoreAssetRef,
  DataType,
  FileKind,
  InputSchema,
  ModelService,
  OutputSchema,
  ParameterConstraint,
  ParameterSchema,
  ParameterValue,
  Seed,
} from 'web';
import type { Schema } from '@/types/node';

interface ImageResult {
  type: 'image';
  width: number;
  height: number;
  url: string;
  id: string | undefined;
}
interface VideoResult {
  type: 'video';
  url: string;
  width: number;
  height: number;
  duration: number;
  fps: number;
  id: string | undefined;
}

const logger = log.getLogger('nodeInputValidation');

export function validateParameterValue(
  value: unknown,
  defaultValue: unknown,
  property: Schema,
): ParameterValue | undefined {
  if (
    value !== undefined &&
    value !== null &&
    !Array.isArray(value) &&
    typeof value === 'object' &&
    'value' in value &&
    property.type !== 'seed' &&
    property.type !== 'fal_image_size'
  ) {
    value = value.value;
  }

  switch (property.type) {
    case 'number':
    case 'input-float':
    case 'input-number': {
      const number = validateNumber(value);
      if (number !== undefined) {
        return {
          type: 'float',
          value: number,
        };
      }
      if (defaultValue === undefined) {
        return {
          type: 'float',
          value: validateNumber(property.min) ?? 0.0,
        };
      }
      break;
    }

    case 'integer':
    case 'input': // backward compatability 24.10 (adding separation between input of integer and floats)
    case 'input-integer': {
      const number = validateNumber(value);
      if (number !== undefined) {
        return {
          type: 'integer',
          value: Math.round(number),
        };
      }
      if (defaultValue === undefined) {
        return {
          type: 'integer',
          value: validateInteger(property.min) ?? 0,
        };
      }
      break;
    }
    case 'boolean': {
      const boolean = validateBoolean(value);
      if (boolean !== undefined) {
        return {
          type: 'boolean',
          value: boolean,
        };
      } else if (defaultValue === undefined) {
        return {
          type: 'boolean',
          value: false,
        };
      }
      break;
    }
    case 'seed': {
      const seed = validateSeed(value);
      if (seed !== undefined) {
        return {
          type: 'seed',
          value: seed,
        };
      }
      if (defaultValue === undefined) {
        return {
          type: 'seed',
          value: {
            seed: 1,
            isRandom: false,
          },
        };
      }
      break;
    }
    case 'enum': {
      const string = validateString(value);
      if (string !== undefined && property.options?.includes(string)) {
        return {
          type: 'string',
          value: string,
        };
      }
      const number = validateNumber(value);
      if (number !== undefined && property.options?.some((item) => Number(item) === number)) {
        return {
          type: 'integer',
          value: Math.round(number),
        };
      }
      if (defaultValue === undefined && property.options && property.options.length > 0) {
        const defaultValue = property.options[0];
        return typeof defaultValue === 'number'
          ? {
              type: 'integer',
              value: Math.round(defaultValue),
            }
          : {
              type: 'string',
              value: defaultValue,
            };
      }
      break;
    }
    case 'string':
    case 'text': {
      const string = validateString(value);
      if (string !== undefined) {
        return {
          type: 'string',
          value: string,
        };
      } else if (defaultValue === undefined) {
        return {
          type: 'string',
          value: '',
        };
      }
      break;
    }
    case 'array': {
      if (value === undefined && defaultValue === undefined) {
        return { type: 'string_array', value: [] };
      }
      const asArray = Array.isArray(value)
        ? value
        : typeof value === 'string'
          ? value === ''
            ? []
            : value.split(',').map((item) => item.trim())
          : [value];
      return {
        type: 'string_array',
        value: asArray.filter((item) => item !== undefined && item !== null).map((item) => item.toString()),
      };
    }
    case 'fal_image_size': {
      if (
        value &&
        typeof value === 'object' &&
        'width' in value &&
        'height' in value &&
        typeof value.width === 'number' &&
        typeof value.height === 'number'
      ) {
        return {
          type: 'image_size',
          value: {
            type: 'custom',
            width: value.width,
            height: value.height,
          },
        };
      } else if (typeof value === 'string') {
        return {
          type: 'image_size',
          value: {
            type: 'built_in',
            value: value,
          },
        };
      } else if (value === null) {
        return {
          type: 'image_size',
          value: {
            type: 'built_in',
            value: 'match_input',
          },
        };
      }
      break;
    }
    // Vector-valued params. The schema type fixes the arity; `color` allows either rgb (vec3) or
    // rgba (vec4), so it's disambiguated by the value's length.
    case 'point': {
      const arr = validateNumberArray(value);
      if (arr && arr.length === 2) {
        return { type: 'vec2', value: [arr[0], arr[1]] };
      }
      break;
    }
    case 'point_radius': {
      const arr = validateNumberArray(value);
      if (arr && arr.length === 3) {
        return { type: 'vec3', value: [arr[0], arr[1], arr[2]] };
      }
      break;
    }
    case 'angle_radius':
    case 'line': {
      const arr = validateNumberArray(value);
      if (arr && arr.length === 4) {
        return { type: 'vec4', value: [arr[0], arr[1], arr[2], arr[3]] };
      }
      break;
    }
    case 'color': {
      const arr = validateNumberArray(value);
      if (arr && arr.length === 4) {
        return { type: 'vec4', value: [arr[0], arr[1], arr[2], arr[3]] };
      }
      if (arr && arr.length === 3) {
        return { type: 'vec3', value: [arr[0], arr[1], arr[2]] };
      }
      break;
    }
    case 'file':
      return undefined;
    case undefined:
      return undefined;
    default: {
      const _exhaustiveCheck: never = property.type;
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      logger.error(`Invalid parameter type: ${property.type}`);
      return undefined;
    }
  }

  if (defaultValue !== undefined) {
    return validateParameterValue(defaultValue, undefined, property);
  }

  logger.error(
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    `Failed to migrate property ${property.type} with value: ${value} and default value: ${defaultValue}`,
  );
  return undefined;
}

export function validateFile(val: unknown): FileKind | undefined {
  const obj = validateObject(val);
  if (!obj) return undefined;

  const type = validateObjectField(obj, 'type', validateString);
  const name = validateObjectField(obj, 'name', validateString);
  const blobstore = validateObjectField(obj, 'blobstore', validateBlobstore);
  const mime = validateObjectField(obj, 'mime', validateString);
  const sizeBytes = validateObjectField(obj, 'sizeBytes', validateNumber);

  if (type === 'image') {
    const image = validateImage(obj);
    if (!image) return undefined;
    const thumbnailUrl = validateObjectField(obj, 'thumbnailUrl', validateString);
    const publicId = validateObjectField(obj, 'publicId', validateString);
    return { ...image, thumbnailUrl, publicId, name, blobstore, mime, sizeBytes };
  }
  if (type === 'video') {
    const video = validateVideo(obj);
    if (!video) return undefined;
    const thumbnailUrl = validateObjectField(obj, 'thumbnailUrl', validateString);
    const publicId = validateObjectField(obj, 'publicId', validateString);
    const hasAudio = validateObjectField(obj, 'hasAudio', validateBoolean);
    return { ...video, thumbnailUrl, publicId, hasAudio: hasAudio ?? true, name, blobstore, mime, sizeBytes };
  }
  if (type === '3D') {
    return validate3D(obj);
  }
  if (type === 'audio') {
    return validateAudio(obj);
  }
  return undefined;
}

function isDataUrl(url: string | undefined): boolean {
  return typeof url === 'string' && url.startsWith('data:');
}

function validateImage(val: unknown): ImageResult | undefined {
  const obj = validateObject(val);
  if (!obj) return;

  const url = validateObjectField(obj, 'url', validateString);
  const width = validateObjectField(obj, 'width', validateNumber);
  const height = validateObjectField(obj, 'height', validateNumber);
  const id = validateObjectField(obj, 'id', validateString);
  if (url && !isDataUrl(url) && !id) {
    logger.warn('AssetIdValidationError: missing id in validateImage', obj);
  }
  return url && width && height
    ? { type: 'image', url: url, width: Math.round(width), height: Math.round(height), id: id }
    : undefined;
}

const DEFAULT_FPS = 30;
const VALID_VIDEO_EXTENSIONS = [
  '.mp4',
  '.webm',
  '.mov',
  '.avi',
  '.flv',
  '.3gp',
  '.mpg',
  '.mpeg',
  '.wmv',
  '.ogg',
  '.avchd',
  '.mkv',
  '.f4v',
];

const VALID_AUDIO_EXTENSIONS = ['.mp3', '.wav', '.ogg'];

function validateVideo(obj: object): VideoResult | undefined {
  const url = validateObjectField(obj, 'url', validateString);
  const width = validateObjectField(obj, 'width', validateNumber);
  const height = validateObjectField(obj, 'height', validateNumber);
  const duration = validateObjectField(obj, 'duration', validateNumber);
  const fps = validateObjectField(obj, 'fps', validateNumber);
  const id = validateObjectField(obj, 'id', validateString);
  if (typeof fps === 'undefined' && typeof duration === 'undefined' && url) {
    if (!urlCanParse(url)) {
      return undefined;
    }
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    if (!VALID_VIDEO_EXTENSIONS.some((ext) => pathname.includes(ext))) {
      return undefined;
    }
  }
  if (url && !isDataUrl(url) && !id) {
    logger.warn('AssetIdValidationError: missing id in validateVideo', obj);
  }
  // Do not use shorthand properties - issue with Edge browser
  return url && width && height
    ? {
        type: 'video',
        url: url,
        width: Math.round(width),
        height: Math.round(height),
        duration: duration ?? 1,
        fps: fps !== undefined && fps !== null && !isNaN(fps) && fps > 0 ? fps : DEFAULT_FPS,
        id: id,
      }
    : undefined;
}

function validate3D(obj: object): FileKind | undefined {
  const url = validateObjectField(obj, 'url', validateString);
  const thumbnailUrl = validateObjectField(obj, 'thumbnailUrl', validateString);
  const publicId = validateObjectField(obj, 'publicId', validateString);
  const id = validateObjectField(obj, 'id', validateString);
  const name = validateObjectField(obj, 'name', validateString);
  if (url && !isDataUrl(url) && !id) {
    logger.warn('AssetIdValidationError: missing id in validate3D', obj);
  }
  const blobstore = validateObjectField(obj, 'blobstore', validateBlobstore);
  const mime = validateObjectField(obj, 'mime', validateString);
  const sizeBytes = validateObjectField(obj, 'sizeBytes', validateNumber);
  // Do not use shorthand properties - issue with Edge browser
  return url
    ? { type: '3D', url: url, thumbnailUrl: thumbnailUrl, publicId: publicId, id: id, name, blobstore, mime, sizeBytes }
    : undefined;
}

function validateAudio(obj: object): ({ type: 'audio' } & AudioFile) | undefined {
  const url = validateObjectField(obj, 'url', validateString);
  const publicId = validateObjectField(obj, 'publicId', validateString);
  const id = validateObjectField(obj, 'id', validateString);
  const name = validateObjectField(obj, 'name', validateString);
  const duration = validateObjectField(obj, 'duration', validateNumber);
  const mime = validateObjectField(obj, 'mime', validateString);
  if (typeof duration === 'undefined' && url && !mime?.startsWith('audio/')) {
    if (!urlCanParse(url)) {
      return undefined;
    }
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    if (!VALID_AUDIO_EXTENSIONS.some((ext) => pathname.includes(ext))) {
      logger.warn('Invalid audio extension: ' + pathname);
      return undefined;
    }
  }
  if (url && !isDataUrl(url) && !id) {
    logger.warn('AssetIdValidationError: missing id in validateAudio', obj);
  }

  const blobstore = validateObjectField(obj, 'blobstore', validateBlobstore);
  const sizeBytes = validateObjectField(obj, 'sizeBytes', validateNumber);
  // Do not use shorthand properties - issue with Edge browser
  return url
    ? {
        type: 'audio',
        url: url,
        publicId: publicId,
        id: id,
        name: name,
        duration: duration ?? 5,
        blobstore,
        mime,
        sizeBytes,
      }
    : undefined;
}

function validateBlobstore(val: unknown): BlobstoreAssetRef | undefined {
  const obj = validateObject(val);
  const assetId = obj && validateObjectField(obj, 'id', validateString);
  const createdAt = obj && validateObjectField(obj, 'createdAt', validateString);
  // A ref is kept only whole, and only its durable fields, never the transient signed urls.
  if (!assetId || !createdAt) return undefined;
  return { id: assetId, createdAt };
}

function validateObject(val: unknown): object | undefined {
  return typeof val === 'object' && val !== null ? val : undefined;
}
function validateObjectField<T>(obj: object, key: string, validator: (v: unknown) => T): T | undefined {
  return key in obj ? validator(obj[key]) : undefined;
}

export function validateNumber(val: unknown): number | undefined {
  return typeof val === 'number' ? val : undefined;
}

export function validateInteger(val: unknown): number | undefined {
  return typeof val === 'number' ? Math.round(val) : undefined;
}

function validateArray(val: unknown): unknown[] | undefined {
  const obj = validateObject(val);
  if (!obj) return undefined;
  return Array.isArray(obj) ? obj : undefined;
}

function validateBoolean(val: unknown): boolean | undefined {
  if (typeof val === 'boolean') return val;
  if (typeof val === 'object' && val !== null && 'value' in val && typeof val.value === 'boolean') return val.value;
  return undefined;
}

function validateSeed(val: unknown): Seed | undefined {
  if (
    typeof val === 'object' &&
    val !== null &&
    'seed' in val &&
    typeof val.seed === 'number' &&
    'isRandom' in val &&
    typeof val.isRandom === 'boolean'
  )
    return { seed: val.seed, isRandom: val.isRandom };
  return undefined;
}

export function validateString(val: unknown): string | undefined {
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val !== null && 'value' in val && typeof val.value === 'string') return val.value;
  return undefined;
}

export function validateStringArray(val: unknown): string[] | undefined {
  const arr = validateArray(val);
  if (!arr) return undefined;
  if (arr.every((item) => validateString(item) !== undefined)) {
    return arr as string[];
  }
}

export function validateNumberArray(val: unknown): number[] | undefined {
  const arr = validateArray(val);
  if (!arr) return undefined;
  if (arr.every((item) => validateNumber(item) !== undefined)) {
    return arr as number[];
  }
}

/** Serde throws across the wasm boundary on any other value. */
const KNOWN_MODEL_SERVICES: readonly string[] = ['replicate', 'civit', 'fal_imported'];

function validateModelService(service: string | undefined): ModelService | undefined {
  return service !== undefined && KNOWN_MODEL_SERVICES.includes(service) ? (service as ModelService) : undefined;
}

/**
 * Sanitizes an {@link AiModelInfo} stored in node data, which is otherwise handed to WASM verbatim.
 * Returns `undefined` when the caller should fall back to {@link validateAiModel}.
 */
export function validateStoredAiModel(model: unknown): AiModelInfo | undefined {
  const validatedModel = validateObject(model);
  if (!validatedModel) return undefined;

  const type = validateObjectField(validatedModel, 'type', validateString);
  if (!type) return undefined;
  if (type === 'none') return { type: 'none' };

  const service = validateObjectField(validatedModel, 'service', validateString);
  if (validateModelService(service)) return validatedModel as unknown as AiModelInfo;

  // Only `predefined` declares an optional service in Rust, so only it can shed an unknown one.
  if (type !== 'predefined') return undefined;
  const { service: _unknownService, ...rest } = validatedModel as Record<string, unknown>;
  return rest as unknown as AiModelInfo;
}

export function validateAiModel(data: Partial<ModelBaseNodeData>): AiModelInfo {
  const validatedModel = validateObject(data.model);
  if (!validatedModel) return { type: 'none' };

  const name = validateObjectField(validatedModel, 'name', validateString);
  if (!name) return { type: 'none' };

  const validatedService = validateModelService(validateObjectField(validatedModel, 'service', validateString));
  const validatedVersion = validateObjectField(validatedModel, 'version', validateString);
  const originalImportSource = validateObjectField(validatedModel, 'originalImportSource', validateString);

  const isUserImported =
    (data as any).isWildCard === true || (originalImportSource !== undefined && originalImportSource !== '');

  if (isUserImported) {
    if (!validatedVersion || !validatedService) {
      logger.error('Wildcard model detected, but the data is not valid: ' + JSON.stringify(data.model));
      return { type: 'none' };
    }
    return {
      type: 'imported',
      name: name,
      version: validatedVersion,
      service: validatedService,
      originalImportSource: originalImportSource,
      coverImage: validateObjectField(validatedModel, 'coverImage', validateString),
      description: data.description,
    };
  }

  return {
    type: 'predefined',
    name: name,
    version: validatedVersion,
    service: validatedService,
    description: data.description,
  };
}

export const validateDataType = (type: unknown): DataType | undefined => {
  if (typeof type === 'string') {
    if (type === 'mask') {
      return 'image';
    }
    if (anyValidTypes.includes(type as DataType)) {
      return type as DataType;
    }
  }
  return undefined;
};

const anyValidTypes: DataType[] = [
  'image',
  'video',
  'audio',
  '3D',
  'text',
  'number',
  'boolean',
  'seed',
  'array',
  'lora',
  'kling-element',
  'keyframe',
];

/**
 * Extra data types an input accepts beyond the single type recorded on its handle, or undefined
 * when the handle's own type is the whole story.
 *
 * World Labs builds a world from one image, a set of images, or a video, so its media slots take
 * either. Its handle keys stay `image_*` because the request consolidation is keyed off them.
 */
export function widenedInputDataTypes(modelName: string | undefined | null, inputId: string): DataType[] | undefined {
  if (modelName === 'kling/v4/reference-to-video' && /^element_\d+$/.test(inputId)) {
    return ['kling-element', 'video'];
  }
  if (modelName !== ModelType.WorldLabs01.valueOf()) return undefined;
  if (inputId === 'image_url' || /^image_\d+$/.test(inputId)) return ['image', 'video'];
  return undefined;
}

/**
 * Resolves the model identifier from node data, accounting for both
 * the deprecated `model.name` field and the newer `kind` system.
 */
export function getModelIdentifier(nodeData: ModelBaseNodeData): string | null {
  if (nodeData.model?.name) {
    return nodeData.model.name;
  }

  const aiData = nodeData as AiModelData;
  if (!aiData.kind) {
    return null;
  }

  if (aiData.kind.type === 'wildcard' && aiData.kind.model) {
    const modelInfo = aiData.kind.model;
    if ('name' in modelInfo && modelInfo.name) {
      return modelInfo.name;
    }
  }

  if (aiData.kind.type !== 'wildcard') {
    return aiData.kind.type;
  }

  return null;
}

export function validateInputs(
  inputHandles?: ModelBaseNodeData['handles']['input'],
  schema?: ModelBaseNodeData['schema'],
  modelName?: string | null,
): InputSchema[] {
  const inputs: InputSchema[] = [];
  if (inputHandles) {
    if (Array.isArray(inputHandles)) {
      for (const id of inputHandles) {
        const inputSchema = schema?.[id];
        if (inputSchema?.exposed !== true) {
          const validType = validateDataType(inputSchema?.type);
          inputs.push({
            id: id,
            validTypes: widenedInputDataTypes(modelName, id) ?? (!validType ? anyValidTypes : [validType]),
            required: inputSchema?.required ?? false,
            title: inputSchema?.title ?? '',
            description: inputSchema?.description,
          });
        }
      }
    } else {
      for (const [id, input] of Object.entries(inputHandles)) {
        const inputSchema = schema?.[id];
        if (inputSchema?.exposed !== true) {
          const validType = validateDataType(input.type) ?? validateDataType(inputSchema?.type);
          inputs.push({
            id: id,
            validTypes: widenedInputDataTypes(modelName, id) ?? (!validType ? anyValidTypes : [validType]),
            required: inputSchema?.required ?? input.required ?? false,
            title: inputSchema?.title ?? input.label ?? '',
            description: inputSchema?.description ?? input.description,
          });
        }
      }
    }

    const getOrder = (id: string): number => {
      const order = schema?.[id]?.order ?? inputHandles?.[id]?.order;
      return typeof order === 'number' ? order : 0;
    };
    inputs.sort((a, b) => getOrder(a.id) - getOrder(b.id));
  }
  return inputs;
}

export function validateOutputs(
  outputHandles?: ModelBaseNodeData['handles']['output'],
  schema?: ModelBaseNodeData['schema'],
): OutputSchema[] {
  const outputs: OutputSchema[] = [];
  if (outputHandles) {
    if (Array.isArray(outputHandles)) {
      for (const id of outputHandles) {
        const outputSchema = schema?.[id];
        let title = outputSchema?.title;
        if (!title || title === '') {
          title = (id.charAt(0).toUpperCase() + id.slice(1)).replace('_', ' ');
        }
        outputs.push({
          id: id,
          dataType: validateDataType(outputSchema?.type),
          title: title,
          description: outputSchema?.description,
        });
      }
    } else {
      for (const [id, output] of Object.entries(outputHandles)) {
        const outputSchema = schema?.[id];
        let title = outputSchema?.title ?? output.label;
        if (!title || title === '') {
          title = (id.charAt(0).toUpperCase() + id.slice(1)).replace('_', ' ');
        }
        outputs.push({
          id: id,
          dataType: validateDataType(output.type) ?? validateDataType(outputSchema?.type),
          title: title,
          description: outputSchema?.description ?? output.description,
        });
      }
    }

    const getOrder = (id: string): number => {
      const order = schema?.[id]?.order ?? outputHandles?.[id]?.order;
      return typeof order === 'number' ? order : 0;
    };
    outputs.sort((a, b) => getOrder(a.id) - getOrder(b.id));
  }
  return outputs;
}

export function validateParameters(
  inputHandles?: ModelBaseNodeData['handles']['input'],
  params?: ModelBaseNodeData['params'],
  schema?: ModelBaseNodeData['schema'],
): ParameterSchema[] {
  const parameters: ParameterSchema[] = [];
  if (schema) {
    for (const [id, parameter] of Object.entries(schema)) {
      if (
        (inputHandles &&
          (Array.isArray(inputHandles) ? !inputHandles.includes(id) : !Object.keys(inputHandles).includes(id))) ||
        parameter.exposed === true
      ) {
        const defaultValue =
          validateParameterValue(parameter.default, undefined, parameter) ??
          validateParameterValue(params?.[id], parameter.default, parameter);

        if (!defaultValue) {
          logger.error(
            `Unable to parse parameter ${id} with value ${params?.[id]} and schema ${JSON.stringify(parameter)}`,
          );
          continue;
        }

        let constraint: ParameterConstraint;
        switch (defaultValue.type) {
          case 'integer':
            if (parameter.min !== undefined && parameter.max !== undefined) {
              constraint = {
                type: 'integer_with_limits',
                min: parameter.min,
                max: parameter.max,
              };
            } else if (parameter.options !== undefined) {
              constraint = {
                type: 'integer_selector',
                options: parameter.options.map((option) => parseInt(option)).filter(Number.isInteger),
              };
            } else {
              constraint = {
                type: 'integer',
              };
            }
            break;
          case 'float':
            if (parameter.min !== undefined && parameter.max !== undefined) {
              constraint = {
                type: 'float_with_limits',
                min: parameter.min,
                max: parameter.max,
              };
            } else {
              constraint = {
                type: 'float',
              };
            }
            break;
          case 'string':
            if (parameter.options !== undefined) {
              constraint = {
                type: 'enum',
                // Provider enums can mix types (fal's `duration: ["auto", 5, …]`) even though the
                // schema types them as strings, and WASM only accepts strings here.
                options: parameter.options.map((option) => String(option)),
              };
            } else {
              constraint = {
                type: 'string',
              };
            }
            break;
          case 'boolean':
            constraint = {
              type: 'boolean',
            };
            break;
          case 'seed':
            constraint = {
              type: 'seed',
            };
            break;
          case 'string_array':
            constraint = {
              type: 'string_array',
            };
            break;
          case 'image_size':
            constraint = {
              type: 'image_size',
              options: (parameter.options ?? []).map((option) => String(option)),
            };
            break;
          case 'file_array':
            constraint = {
              type: 'file_array',
            };
            break;
          // vecN arity alone is ambiguous, so the gizmo/color constraint is resolved from the
          // schema's declared `type` (see Schema['type']).
          case 'vec2':
            constraint = { type: 'point' };
            break;
          case 'vec3':
            constraint = parameter.type === 'color' ? { type: 'color' } : { type: 'point_radius' };
            break;
          case 'vec4':
            switch (parameter.type) {
              case 'color':
                constraint = { type: 'color' };
                break;
              case 'line':
                constraint = { type: 'line' };
                break;
              default:
                constraint = { type: 'angle_radius' };
                break;
            }
            break;
        }

        parameters.push({
          id: id,
          constraint,
          defaultValue,
          title: parameter.title ?? '',
          description: parameter.description,
        });
      }
    }

    const getOrder = (id: string): number => {
      const order = schema?.[id]?.order ?? inputHandles?.[id]?.order;
      return typeof order === 'number' ? order : 0;
    };
    parameters.sort((a, b) => getOrder(a.id) - getOrder(b.id));
  }
  return parameters;
}
